import React, { useEffect, useMemo } from "react";
import { columns } from "../../../../dtos/columns";
import { ResponseActualizacionDeudaDto } from "../models";
import { Checkbox } from '@mui/material';

interface IProps {
  controlTabla: any;
  idsMutuos: string[]; 
  setIdsMutuos: React.Dispatch<React.SetStateAction<string[]>>;
}

export function useColumnsActualizacionDeuda({ controlTabla, idsMutuos, setIdsMutuos }: IProps) {
  const data: ResponseActualizacionDeudaDto[] = controlTabla?.data ?? [];

  // ✅ Preseleccionar automáticamente filas con porta=true (solo agrega, no quita)
  useEffect(() => {
    if (!data.length) return;
    setIdsMutuos(prev => {
      const sel = new Set(prev);
      for (const r of data) {
        if (r?.porta) {
          const id = String(r?.numerooperacionmutuo ?? "");
          if (id) sel.add(id);
        }
      }
      return Array.from(sel);
    });
  }, [data, setIdsMutuos]);

  // Para que el checkbox del header considere solo la página actual
  const pageIdSet = useMemo(() => new Set<string>(
    data.map(r => String(r?.numerooperacionmutuo ?? "")).filter(Boolean)
  ), [data]);

  const selectedOnPageCount = useMemo(
    () => idsMutuos.filter(id => pageIdSet.has(id)).length,
    [idsMutuos, pageIdSet]
  );

  const COLUMNS: columns<ResponseActualizacionDeudaDto>[] = [
    {
      name: (
        <Checkbox
          size="small"
          checked={data.length > 0 && selectedOnPageCount === data.length}
          indeterminate={selectedOnPageCount > 0 && selectedOnPageCount < data.length}
          onChange={e => {
            if (e.target.checked) {
              // Añadir todos los de la página actual
              setIdsMutuos(prev => Array.from(new Set([
                ...prev,
                ...Array.from(pageIdSet)
              ])));
            } else {
              // Quitar todos los de la página actual (mantener los de otras páginas)
              setIdsMutuos(prev => prev.filter(id => !pageIdSet.has(id)));
            }
          }}
        />
      ),
      selector: (row: ResponseActualizacionDeudaDto) => String(row.numerooperacionmutuo ?? ""),
      cell: (row: ResponseActualizacionDeudaDto) => {
        const id = String(row?.numerooperacionmutuo ?? "");
        return (
          <Checkbox
            size="small"
            checked={idsMutuos.includes(id)}
            onChange={e => {
              setIdsMutuos(prev =>
                e.target.checked
                  ? Array.from(new Set([...prev, id]))
                  : prev.filter(x => x !== id)
              );
            }}
          />
        );
      },
      allowOverflow: true,
      button: true,
      export: false,
      width: "60px",
    },
    {
      name: "Portado anteriormente",
      selector: (row) => row.porta ? "Sí" : "No",
      sortable: true,
      sortField: "porta",
      wrap: true,
      export: true,
    },
    {
      name: "Mutuo",
      selector: (row) => row.mutuoTipo?.toString() || 'CREDITO HIPOTECARIO',
      sortable: true,
      sortField: "mutuoTipo",
      wrap: true,
      export: true,
    },
    {
      name: "Identificación de mutuo",
      selector: (row) => row.numerooperacionmutuo?.toString() || '',
      sortable: true,
      sortField: "numerooperacionmutuo",
      wrap: true,
      export: true,
    },
    {
      name: "ID complementario",
      selector: (row) => row.numerooperacionmutuocomple?.toString() || '',
      sortable: true,
      sortField: "numerooperacionmutuocomple",
      wrap: true,
      export: true,
    },
  ];

  return { COLUMNS };
}
